import { FC, ReactNode } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { X } from 'lucide-react';

interface DrawerProps {
  open: boolean;
  onClose: (value: boolean) => void;
  title?: string;
  header?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  sidePanel?: ReactNode;
  isSidePanelOpen?: boolean;
}

export const Drawer: FC<DrawerProps> = ({
  open,
  onClose,
  title,
  header,
  children,
  footer,
  className,
  sidePanel,
  isSidePanelOpen = false,
}) => {
  return (
    <Dialog open={open} onClose={() => {}} className="relative">
      <DialogBackdrop
        transition
        onClick={() => onClose(false)}
        className="fixed inset-0 z-60 bg-gray-500/75 transition-opacity duration-500 ease-in-out data-closed:opacity-0"
      />

      <div className="pointer-events-none fixed inset-0 z-62 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div
            className={`pointer-events-none fixed inset-y-0 right-0 flex max-w-full ${
              sidePanel && isSidePanelOpen ? 'left-0' : 'pl-10 sm:pl-16'
            }`}
          >
            {sidePanel && isSidePanelOpen && (
              <div className="pointer-events-auto fixed inset-0 z-10 flex min-w-0 flex-col overflow-hidden bg-(--neutral-white-NW1000) shadow-xl sm:static sm:z-auto sm:my-[20px] sm:mx-[20px] sm:flex-1 sm:rounded-lg">
                {sidePanel}
              </div>
            )}
            <DialogPanel
              transition
              className={`pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-closed:translate-x-full sm:duration-700 ${className ?? ''}`}
            >
              <div className="relative flex h-full flex-col bg-(--neutral-white-NW1000) shadow-xl">
                {header ? (
                  <div className="shrink-0">{header}</div>
                ) : (
                  <div className="flex shrink-0 items-center justify-between px-4 sm:px-6 py-6 border-b border-(--neutral-white-NW800)">
                    {title && (
                      <DialogTitle className="[font-family:var(--font-arvo)]! [font-size:var(--fs-h5)]! font-bold! text-(--neutral-black-NB1000)! not-italic!">
                        {title}
                      </DialogTitle>
                    )}

                    <div className="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        onClick={() => onClose(false)}
                        className="relative rounded-md text-(--neutral-black-NB500) hover:text-(--neutral-black-NB900) cursor-pointer"
                      >
                        <span className="absolute -inset-2.5" />
                        <span className="sr-only">Close panel</span>
                        <X aria-hidden="true" className="size-6" />
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{children}</div>

                {footer && (
                  <div className="shrink-0 px-4 py-4 border-t border-(--neutral-white-NW800)">
                    {footer}
                  </div>
                )}
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
